import { Component, Output, EventEmitter, Input, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ConfirmationModalComponent } from '../confirmation-modal/confirmation-modal';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-scan-qr-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './scan-qr-modal.html',
  styleUrl: './scan-qr-modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScanQrModal implements OnDestroy {
  @Input() name = 'Mykola Ledenov';
  @Input() phone = '+91 22 12345678';
  @Input() email = 'ledenov@upi.com';
  @Input() qrSrc = 'assets/icons/img_rectangle-240648084.png';

  @Output() close = new EventEmitter<void>();
  @Output() share = new EventEmitter<void>();
  @Output() download = new EventEmitter<void>();

  private confirmationOverlayRef: OverlayRef | null = null;
  private overlaySubs = new Subscription();

  constructor(
    private overlay: Overlay,
    private cdr: ChangeDetectorRef
  ) { }

  private createGlobalOverlay(): OverlayRef {
    return this.overlay.create({
      hasBackdrop: false,
      positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
      scrollStrategy: this.overlay.scrollStrategies.block(),
    });
  }

  onClose() {
    // Show confirmation dialog before closing
    if (!this.confirmationOverlayRef) {
      this.confirmationOverlayRef = this.createGlobalOverlay();
      const portal = new ComponentPortal(ConfirmationModalComponent);
      const componentRef = this.confirmationOverlayRef.attach(portal);

      componentRef.instance.title = 'Cancel changes?';
      componentRef.instance.message = 'All data will be erased irrevocably.';
      componentRef.instance.icon = 'assets/icons/img_combo-shape.svg';
      componentRef.instance.closeLabel = 'Close';
      componentRef.instance.confirmLabel = 'Cancel';
      componentRef.instance.confirmColor = 'danger';

      this.overlaySubs.add(componentRef.instance.cancel.subscribe(() => this.closeConfirmation()));
      this.overlaySubs.add(componentRef.instance.confirm.subscribe(() => {
        this.closeConfirmation();
        this.close.emit();
      }));
    }
    this.cdr.markForCheck();
  }

  private closeConfirmation() {
    if (this.confirmationOverlayRef) {
      this.confirmationOverlayRef.dispose();
      this.confirmationOverlayRef = null;
    }
    this.overlaySubs.unsubscribe();
    this.overlaySubs = new Subscription();
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.overlaySubs.unsubscribe();
    if (this.confirmationOverlayRef) {
      this.confirmationOverlayRef.dispose();
      this.confirmationOverlayRef = null;
    }
  }

  onShare() {
    // emit for parent or try navigator.share if available
    if ((navigator as any).share) {
      try {
        (navigator as any).share({ title: this.name, text: 'Pay via UPI', url: this.qrSrc });
        this.share.emit();
      } catch (e) {
        this.share.emit();
      }
    } else {
      this.share.emit();
    }
  }

  onDownload() {
    // Simple download implementation — may be blocked for cross-origin images
    this.download.emit();
    const link = document.createElement('a');
    link.href = this.qrSrc;
    link.download = 'qr-code.png';
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

}
